import { Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { type NamirialSigningModuleSpecsFragment } from '../../../../../api/fragments/NamirialSigningModuleSpecs';
import {
    CreateNamirialSigningModuleDocument,
    type CreateNamirialSigningModuleMutation,
    type CreateNamirialSigningModuleMutationVariables,
} from '../../../../../api/mutations/createNamirialSigningModule';
import { type NamirialSettingsInput, type NamirialSigningModuleSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import PasswordField from '../../../../../components/fields/PasswordField';
import validators from '../../../../../utilities/validators';
import NamirialSigningModulePage, { type NamirialSigningModuleProps } from '../../NamirialSigningModuleDetailPage';
import { type ModuleImplementation } from './types';

type NamirialSigningModuleImplementation = ModuleImplementation<
    NamirialSigningModuleSpecsFragment,
    NamirialSigningModuleSettings & NamirialSettingsInput
>;

const formValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('endpoint'),
    validators.requiredString('apiToken')
);

const Form: NamirialSigningModuleImplementation['FormComponent'] = () => {
    const { t } = useTranslation(['moduleDetails']);

    return (
        <Row gutter={10}>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <InputField {...t('moduleDetails:fields.endpoint', { returnObjects: true })} name="endpoint" />
            </Col>
            <Col md={8} xs={24}>
                <PasswordField {...t('moduleDetails:fields.apiToken', { returnObjects: true })} name="apiToken" />
            </Col>
        </Row>
    );
};

const implementation: NamirialSigningModuleImplementation = {
    PageComponent: ({ module }: NamirialSigningModuleProps) => <NamirialSigningModulePage module={module} />,
    getTitle: t => t('moduleDetails:implementations.NamirialSigningModule.className'),
    FormComponent: Form,
    getValidator: () => formValidator,
    create: async (apolloClient, companyId, { displayName, ...secrets }) =>
        apolloClient
            .mutate<CreateNamirialSigningModuleMutation, CreateNamirialSigningModuleMutationVariables>({
                mutation: CreateNamirialSigningModuleDocument,
                variables: { companyId, settings: { displayName }, secrets },
            })
            .then(({ data }) => data.module),
};

export default implementation;
